import { useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, CheckCircle2, MessageCircle, RotateCcw } from 'lucide-react';
import { PROVINCES, HOME_SIZES } from '../data/content';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: '',
  phone: '',
  from_city: '',
  to_city: '',
  home_size: '',
  move_date: '',
  note: '',
};

const inputCls =
  'w-full bg-[#060B19] border border-[#1E293B] focus:border-[#FF5C00] focus:outline-none rounded-xl px-4 py-3.5 text-sm text-[#F2F4F8] placeholder:text-[#A0ABC0]/60 transition-colors duration-300';

const labelCls = 'block text-[11px] uppercase tracking-[0.2em] text-[#A0ABC0] mb-2';

const buildWhatsAppMessage = (f) =>
  `Merhaba, taşıma teklifi istiyorum.%0A%0A` +
  `Ad: ${encodeURIComponent(f.name)}%0A` +
  `Telefon: ${encodeURIComponent(f.phone)}%0A` +
  `Nereden: ${encodeURIComponent(f.from_city)}%0A` +
  `Nereye: ${encodeURIComponent(f.to_city)}%0A` +
  `Ev: ${encodeURIComponent(f.home_size)}%0A` +
  (f.move_date ? `Tarih: ${encodeURIComponent(f.move_date)}%0A` : '') +
  (f.note ? `Not: ${encodeURIComponent(f.note)}` : '');

export const QuoteForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name || !form.phone || !form.from_city || !form.to_city || !form.home_size) {
      setError('Lütfen zorunlu alanları doldurun.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setDone(true);
    } catch {
      setError('Gönderim başarısız oldu. Lütfen tekrar deneyin veya bizi arayın.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="quote-form-wrapper"
      className="bg-[#0A1128] border border-[#1E293B] rounded-2xl p-8 md:p-12"
    >
      <AnimatePresence mode="wait">
        {done ? (
          <motion.div
            key="success"
            data-testid="quote-success-message"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-10"
          >
            <CheckCircle2 size={56} className="text-[#25D366] mx-auto mb-6" />
            <h3 className="font-display text-2xl md:text-3xl tracking-tight mb-3">
              Talebiniz alındı
            </h3>
            <p className="text-[#A0ABC0] max-w-md mx-auto mb-8">
              En kısa sürede sizi arayacağız. Daha hızlı dönüş için talebinizi WhatsApp&apos;tan da
              iletebilirsiniz.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href={'https://wa.me/905339166108?text=' + buildWhatsAppMessage(form)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="quote-whatsapp-send-button"
                className="inline-flex items-center gap-2.5 bg-[#25D366] hover:bg-[#1fb457] text-white font-bold rounded-full px-7 py-3.5 transition-colors duration-300"
              >
                <MessageCircle size={17} />
                WhatsApp&apos;tan Gönder
              </a>
              <button
                onClick={() => {
                  setForm(EMPTY);
                  setDone(false);
                }}
                data-testid="quote-new-request-button"
                className="inline-flex items-center gap-2 border border-[#1E293B] hover:border-[#FF5C00] text-[#F2F4F8] rounded-full px-7 py-3.5 transition-colors duration-300"
              >
                <RotateCcw size={15} />
                Yeni Talep
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            data-testid="quote-form"
            onSubmit={submit}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-5"
          >
            <div>
              <label className={labelCls} htmlFor="q-name">Ad Soyad *</label>
              <input
                id="q-name"
                data-testid="quote-name-input"
                className={inputCls}
                placeholder="Adınız Soyadınız"
                value={form.name}
                onChange={set('name')}
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="q-phone">Telefon *</label>
              <input
                id="q-phone"
                data-testid="quote-phone-input"
                className={inputCls}
                placeholder="05XX XXX XX XX"
                type="tel"
                value={form.phone}
                onChange={set('phone')}
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="q-from">Nereden *</label>
              <select
                id="q-from"
                data-testid="quote-from-select"
                className={inputCls}
                value={form.from_city}
                onChange={set('from_city')}
              >
                <option value="">İl seçin</option>
                {PROVINCES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls} htmlFor="q-to">Nereye *</label>
              <select
                id="q-to"
                data-testid="quote-to-select"
                className={inputCls}
                value={form.to_city}
                onChange={set('to_city')}
              >
                <option value="">İl seçin</option>
                {PROVINCES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls} htmlFor="q-size">Ev / Eşya Büyüklüğü *</label>
              <select
                id="q-size"
                data-testid="quote-size-select"
                className={inputCls}
                value={form.home_size}
                onChange={set('home_size')}
              >
                <option value="">Seçin</option>
                {HOME_SIZES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls} htmlFor="q-date">Taşınma Tarihi</label>
              <input
                id="q-date"
                data-testid="quote-date-input"
                className={`${inputCls} [color-scheme:dark]`}
                type="date"
                value={form.move_date}
                onChange={set('move_date')}
              />
            </div>
            <div className="md:col-span-2">
              <label className={labelCls} htmlFor="q-note">Notunuz</label>
              <textarea
                id="q-note"
                data-testid="quote-note-input"
                className={`${inputCls} min-h-[100px] resize-y`}
                placeholder="Asansör ihtiyacı, kat bilgisi, özel eşyalar..."
                value={form.note}
                onChange={set('note')}
              />
            </div>

            {error && (
              <p data-testid="quote-error-message" className="md:col-span-2 text-sm text-[#FF5C00]">
                {error}
              </p>
            )}

            <div className="md:col-span-2 flex flex-wrap items-center gap-5 pt-2">
              <button
                type="submit"
                disabled={loading}
                data-testid="quote-submit-button"
                className="group inline-flex items-center gap-3 bg-[#FF5C00] hover:bg-[#E65000] disabled:opacity-60 text-[#0A1128] font-bold rounded-full px-8 py-4 transition-colors duration-300"
              >
                <Send size={17} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                {loading ? 'Gönderiliyor...' : 'Teklif İste'}
              </button>
              <span className="text-xs text-[#A0ABC0]">
                Gönderdiğinizde ekibimiz sizi telefonla arar.
              </span>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};
